import type { ReactNode } from "react";
import { cx } from "../lib/cx";

export interface PrimaryCellProps {
	/** The row's name — bold navy ink. */
	children: ReactNode;
	/** Optional grey line under it (a code, an address). Truncates; the full text goes in `title`. */
	sub?: ReactNode;
	/** Tooltip for the whole cell — pass the untruncated text. */
	title?: string;
	className?: string;
}

/**
 * The identifying cell of a `variant="list"` table (prototype `.tbl .who`):
 * a 13.5px semibold name in navy ink with a 12px grey sub-line.
 */
export function PrimaryCell({ children, sub, title, className }: PrimaryCellProps) {
	return (
		<span className={cx("flex min-w-0 flex-col gap-[0.125rem]", className)} title={title}>
			<b className="text-[0.84375rem] leading-[1.25] font-semibold text-gms-navy-ink">{children}</b>
			{sub !== undefined && sub !== null && sub !== "" && (
				<span className="truncate text-[0.75rem] leading-[1.3] text-gms-ink-3">{sub}</span>
			)}
		</span>
	);
}

/**
 * A small label in a list cell (prototype `.tag`): 4px radius, navy-soft,
 * 10.5px semibold. Never wraps out of its box — the label stays on one line
 * and the cell gives it room (`whitespace-nowrap`); a label longer than the
 * column truncates with the full text in the tooltip.
 */
export function ListTag({ children, title, className }: { children: ReactNode; title?: string; className?: string }) {
	return (
		<span
			title={title}
			className={cx(
				"inline-flex max-w-full items-center rounded-[0.25rem] bg-gms-navy-soft px-[0.4375rem] py-[0.125rem] text-[0.65625rem] leading-[1.4] font-semibold tracking-[0.02em] whitespace-nowrap text-gms-navy",
				className,
			)}
		>
			<span className="truncate">{children}</span>
		</span>
	);
}
